import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Gift, Loader2, Users, Send, PackageOpen, CheckCircle2 } from 'lucide-react';
import { getApiBaseUrl } from '../lib/apiBaseUrl';
import {
  CoinRainOverlay,
  createCoinParticles,
  playDailyRewardSound,
  DEFAULT_REWARD_COIN_SRC,
} from '../lib/rewardClaimCelebration';

const API_BASE_URL = getApiBaseUrl();
const COIN_SRC = DEFAULT_REWARD_COIN_SRC;

async function parseJsonSafe(resp) {
  const ctype = String(resp.headers.get('content-type') || '').toLowerCase();
  if (!ctype.includes('application/json')) {
    const text = await resp.text();
    const preview = String(text || '').slice(0, 120).replace(/\s+/g, ' ').trim();
    const err = new Error(
      `API вернул не JSON (status ${resp.status}). Проверь REACT_APP_API_URL/proxy. ${preview || 'Пустой ответ'}`
    );
    err.code = 'BAD_CONTENT_TYPE';
    throw err;
  }
  return resp.json();
}

function formatCooldown(msRaw) {
  const ms = Math.max(0, Math.floor(Number(msRaw) || 0));
  const totalMins = Math.ceil(ms / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h <= 0) return `${m}м`;
  return `${h}ч ${m}м`;
}

function formatTs(ts) {
  return new Date(Number(ts) || Date.now()).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function LobbyFriendGiftsScreen({ onBack, sessionToken, onToast, onBalanceUpdate, onIncomingCountChange }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [claimingId, setClaimingId] = useState('');
  const [claimingAll, setClaimingAll] = useState(false);
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');
  const [coinBurst, setCoinBurst] = useState(null);
  const burstTimeoutRef = useRef(null);

  const toast = useCallback((msg) => {
    if (!msg) return;
    if (typeof onToast === 'function') onToast(msg);
  }, [onToast]);

  const applyState = useCallback((next) => {
    setState(next);
    if (typeof next?.balance === 'number' && Number.isFinite(next.balance)) {
      onBalanceUpdate?.(next.balance);
    }
    if (typeof onIncomingCountChange === 'function') {
      const incoming = Array.isArray(next?.incoming) ? next.incoming : [];
      const pending = incoming.filter((g) => !g?.claimedAt).length;
      onIncomingCountChange(pending);
    }
  }, [onBalanceUpdate, onIncomingCountChange]);

  const fetchState = useCallback(async () => {
    if (!sessionToken) {
      setLoading(false);
      setError('Нужен вход в аккаунт для подарков');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/friends/gifts/state`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const data = await parseJsonSafe(resp);
      if (!resp.ok) throw new Error(String(data?.error || 'Не удалось загрузить подарки'));
      applyState(data);
    } catch (e) {
      setError(String(e?.message || 'Ошибка загрузки'));
    } finally {
      setLoading(false);
    }
  }, [sessionToken, applyState]);

  useEffect(() => {
    void fetchState();
  }, [fetchState]);

  const friends = useMemo(
    () => (Array.isArray(state?.friends) ? state.friends : []),
    [state?.friends]
  );
  const incoming = Array.isArray(state?.incoming) ? state.incoming : [];
  const cooldown = state?.senderCooldown || {};
  const canSend = Boolean(cooldown?.canSend);
  const remainingMs = Math.max(0, Number(cooldown?.remainingMs) || 0);
  const giftAmount = Math.max(1, Math.floor(Number(state?.giftAmount) || 500));
  const availableSelected = useMemo(
    () => selected.filter((id) => friends.some((f) => String(f.id) === String(id))),
    [selected, friends]
  );

  const toggleFriend = (friendId) => {
    const sid = String(friendId);
    setSelected((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
  };

  const toggleAll = () => {
    if (availableSelected.length === friends.length) {
      setSelected([]);
      return;
    }
    setSelected(friends.map((f) => String(f.id)));
  };

  const sendGifts = async (sendAll, explicitIds = null) => {
    if (!sessionToken || submitting) return;
    const targetIds = explicitIds == null ? availableSelected : explicitIds;
    if (!sendAll && targetIds.length === 0) {
      setError('Выберите хотя бы одного кореша');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/friends/gifts/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          sendAll: Boolean(sendAll),
          toUserIds: sendAll ? [] : targetIds,
        }),
      });
      const data = await parseJsonSafe(resp);
      if (!resp.ok) {
        if (data?.code === 'GIFTS_COOLDOWN') {
          throw new Error(`Следующая отправка через ${formatCooldown(data.remainingMs)}`);
        }
        if (data?.code === 'INSUFFICIENT_FUNDS') throw new Error('Недостаточно монет');
        if (data?.code === 'GIFTS_NO_RECIPIENTS') throw new Error('Нет корешей для подгона');
        throw new Error(String(data?.error || 'Не удалось отправить подарки'));
      }
      applyState(data.state);
      setSelected([]);
      toast(`Отправлено подарков: ${Number(data.sentCount) || 0}`);
    } catch (e) {
      setError(String(e?.message || 'Ошибка отправки'));
    } finally {
      setSubmitting(false);
    }
  };

  const claimGift = async (giftId) => {
    if (!sessionToken || !giftId || claimingId) return;
    setClaimingId(String(giftId));
    setError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/friends/gifts/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({ giftId }),
      });
      const data = await parseJsonSafe(resp);
      if (!resp.ok) throw new Error(String(data?.error || 'Не удалось забрать подарок'));
      applyState(data.state);
      const reward = Number(data.amount) || giftAmount;
      const particleCount = Math.min(36, 16 + Math.floor(reward / 350));
      setCoinBurst({
        id: `${Date.now()}-${giftId}`,
        particles: createCoinParticles(particleCount),
      });
      if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current);
      burstTimeoutRef.current = setTimeout(() => setCoinBurst(null), 2100);
      playDailyRewardSound(0.9 + reward / 7000);
      toast(`Вы получили ${reward} монет`);
    } catch (e) {
      setError(String(e?.message || 'Ошибка получения'));
    } finally {
      setClaimingId('');
    }
  };

  const claimAllGifts = async () => {
    if (!sessionToken || claimingAll || claimingId) return;
    const claimable = incoming.filter((g) => !g?.claimedAt).map((g) => String(g.id || '')).filter(Boolean);
    if (!claimable.length) return;
    setClaimingAll(true);
    setError('');
    let okCount = 0;
    let totalReward = 0;
    try {
      for (const gid of claimable) {
        const resp = await fetch(`${API_BASE_URL}/api/friends/gifts/claim`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({ giftId: gid }),
        });
        const data = await parseJsonSafe(resp);
        if (!resp.ok) continue;
        okCount += 1;
        totalReward += Math.max(1, Number(data?.amount) || giftAmount);
        if (data?.state) applyState(data.state);
      }
      if (okCount > 0) {
        const particleCount = Math.min(48, 18 + Math.floor(totalReward / 300));
        setCoinBurst({
          id: `${Date.now()}-all`,
          particles: createCoinParticles(particleCount),
        });
        if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current);
        burstTimeoutRef.current = setTimeout(() => setCoinBurst(null), 2200);
        playDailyRewardSound(0.95 + totalReward / 9000);
        toast(`Получено подарков: ${okCount}`);
      }
      if (okCount !== claimable.length) {
        setError('Часть подарков не удалось получить. Попробуйте ещё раз.');
      }
    } catch (e) {
      setError(String(e?.message || 'Ошибка получения всех подарков'));
    } finally {
      setClaimingAll(false);
    }
  };

  useEffect(() => {
    return () => {
      if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[85] flex flex-col bg-[#05080e] text-foreground"
      role="dialog"
      aria-modal="true"
      aria-labelledby="friend-gifts-title"
    >
      <CoinRainOverlay burst={coinBurst} coinSrc={COIN_SRC} />
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1018] via-[#05080e] to-[#030508]" />
        <div className="absolute right-0 top-1/4 h-48 w-48 rounded-full bg-amber-500/5 blur-3xl" />
      </div>

      <header className="relative z-[1] flex items-center gap-2 border-b border-white/[0.06] bg-black/20 px-3 py-3 pt-[max(0.75rem,env(safe-area-inset-top,0px))] backdrop-blur-md sm:px-4">
        <button
          type="button"
          onClick={onBack}
          aria-label="Назад"
          className="lobby-focus flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.04] text-slate-200 transition hover:border-white/15 hover:bg-white/[0.07]"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </button>
        <div className="min-w-0">
          <h1 id="friend-gifts-title" className="text-sm font-bold uppercase tracking-[0.12em] text-slate-100">
            ПОДГОНЫ ДЛЯ БРАТВЫ
          </h1>
          <p className="text-xs text-slate-400">500 монет за подарок · общий кулдаун 12ч</p>
        </div>
      </header>

      <div className="relative z-[1] flex min-h-0 flex-1 flex-col px-3 pb-3 pt-2 sm:px-4">
        <section className="mb-2 overflow-hidden rounded-2xl border border-slate-300/10 bg-gradient-to-r from-[#151f31] via-[#111a28] to-[#141d2e] shadow-[0_14px_28px_rgba(0,0,0,0.35)]">
          <div className="flex items-center justify-between gap-3 border-b border-white/10 px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-300">Friend Gifts</p>
              <p className="text-sm font-semibold text-white">
                Баланс: <span className="text-amber-300">{Number(state?.balance) || 0}</span>
              </p>
            </div>
            <span
              className={
                canSend
                  ? 'rounded-full border border-emerald-400/45 bg-emerald-500/20 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-100'
                  : 'rounded-full border border-amber-400/45 bg-amber-500/15 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-amber-100'
              }
            >
              {canSend ? 'Доступно' : `Через ${formatCooldown(remainingMs)}`}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
            <button
              type="button"
              disabled={loading || submitting || !canSend || friends.length === 0}
              onClick={() => void sendGifts(false)}
              className="lobby-focus inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] text-slate-100 disabled:opacity-45"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Send className="h-3.5 w-3.5" aria-hidden />}
              Сделать подгон
            </button>
            <button
              type="button"
              disabled={loading || submitting || !canSend || friends.length === 0}
              onClick={() => void sendGifts(true)}
              className="lobby-focus inline-flex items-center gap-1.5 rounded-lg border border-cyan-400/35 bg-cyan-500/15 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] text-cyan-100 disabled:opacity-45"
            >
              <Users className="h-3.5 w-3.5" aria-hidden />
              Сделать подгон всей братве
            </button>
            <button
              type="button"
              disabled={loading || friends.length === 0}
              onClick={toggleAll}
              className="lobby-focus ml-auto rounded-lg border border-white/15 bg-black/20 px-2.5 py-2 text-[11px] font-semibold text-slate-300 disabled:opacity-45"
            >
              {availableSelected.length === friends.length && friends.length > 0 ? 'Снять выделение' : 'Выбрать всех'}
            </button>
          </div>
        </section>

        {error ? (
          <div className="mb-2 rounded-lg border border-rose-500/40 bg-rose-950/30 px-3 py-2 text-xs text-rose-100">{error}</div>
        ) : null}

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-hidden lg:grid-cols-2">
          <section className="flex min-h-0 flex-col rounded-2xl border border-white/[0.08] bg-[#0d1421]/95">
            <header className="flex items-center justify-between gap-2 border-b border-white/[0.08] px-3 py-2.5">
              <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-200">Братва</p>
              <span className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] font-semibold text-slate-300">
                {friends.length}
              </span>
            </header>
            <div className="lobby-mp-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
              {loading ? (
                <div className="flex items-center justify-center py-8 text-slate-400">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Загрузка...
                </div>
              ) : null}
              {!loading && !friends.length ? (
                <p className="rounded-lg border border-white/10 bg-black/20 px-3 py-5 text-center text-sm text-slate-500">
                  Нет корешей для подгона.
                </p>
              ) : null}
              {!loading
                ? friends.map((f) => {
                    const fid = String(f.id || '');
                    const checked = availableSelected.includes(fid);
                    return (
                      <article
                        key={fid}
                        className="group rounded-xl border border-white/10 bg-gradient-to-b from-[#182235] to-[#111927] p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
                      >
                        <div className="flex items-center gap-2">
                          <label className="inline-flex shrink-0 cursor-pointer items-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleFriend(fid)}
                              className="h-4 w-4 accent-cyan-400"
                              aria-label={`Выбрать ${f.username}`}
                            />
                          </label>
                          <div className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/15 bg-slate-800 text-sm font-black text-white">
                            {f.avatarUrl ? (
                              <img src={f.avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              (f.username || 'G').charAt(0).toUpperCase()
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-bold text-white">{f.username}</p>
                            <p className="text-[11px] text-slate-400">Уровень {Math.max(1, Number(f.level) || 1)}</p>
                          </div>
                          <button
                            type="button"
                            disabled={loading || submitting || !canSend}
                            onClick={() => void sendGifts(false, [fid])}
                            className="lobby-focus inline-flex shrink-0 items-center gap-1 rounded-lg border border-amber-400/40 bg-amber-400/20 px-2.5 py-1.5 text-[11px] font-bold text-amber-100 transition hover:bg-amber-400/30 disabled:opacity-45"
                          >
                            <Gift className="h-3.5 w-3.5" aria-hidden />
                            {giftAmount}
                          </button>
                        </div>
                      </article>
                    );
                  })
                : null}
            </div>
          </section>

          <section className="flex min-h-0 flex-col rounded-2xl border border-white/[0.08] bg-[#0d1421]/95">
            <header className="flex items-center justify-between gap-2 border-b border-white/[0.08] px-3 py-2.5">
              <div className="flex items-center gap-2">
                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-200">Входящие</p>
                <span className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] font-semibold text-slate-300">
                  {incoming.length}
                </span>
              </div>
              <button
                type="button"
                disabled={claimingAll || claimingId || incoming.filter((g) => !g?.claimedAt).length === 0}
                onClick={() => void claimAllGifts()}
                className="lobby-focus inline-flex items-center gap-1 rounded-lg border border-emerald-400/40 bg-emerald-500/20 px-2.5 py-1.5 text-[11px] font-bold text-emerald-100 transition hover:bg-emerald-500/30 disabled:opacity-45"
              >
                {claimingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                Забрать все
              </button>
            </header>
            <div className="lobby-mp-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
              {!incoming.length ? (
                <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-5 text-center">
                  <PackageOpen className="mx-auto mb-2 h-6 w-6 text-slate-500" aria-hidden />
                  <p className="text-sm text-slate-500">Пока нет входящих подарков.</p>
                </div>
              ) : null}
              {incoming.map((g) => {
                const claimable = !g.claimedAt;
                const gid = String(g.id || '');
                return (
                  <article
                    key={gid}
                    className="rounded-xl border border-white/10 bg-gradient-to-b from-[#182235] to-[#111927] p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-white">{g.from?.username || 'Игрок'}</p>
                        <p className="text-[11px] text-slate-400">Уровень {Math.max(1, Number(g.from?.level) || 1)}</p>
                        <p className="mt-0.5 text-[10px] text-slate-500">{formatTs(g.createdAt)}</p>
                      </div>
                      <p className="text-base font-black text-amber-300">+{Math.max(1, Number(g.amount) || giftAmount)}</p>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span
                        className={
                          claimable
                            ? 'inline-flex items-center gap-1 rounded-full border border-cyan-400/35 bg-cyan-500/15 px-2 py-0.5 text-[10px] font-semibold text-cyan-100'
                            : 'inline-flex items-center gap-1 rounded-full border border-emerald-400/35 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-100'
                        }
                      >
                        <CheckCircle2 className="h-3 w-3" aria-hidden />
                        {claimable ? 'Готов к получению' : 'Получено'}
                      </span>
                      <button
                        type="button"
                        disabled={!claimable || claimingId === gid}
                        onClick={() => void claimGift(gid)}
                        className="lobby-focus rounded-lg border border-emerald-400/40 bg-emerald-500/20 px-2.5 py-1.5 text-[11px] font-bold text-emerald-100 transition hover:bg-emerald-500/30 disabled:opacity-45"
                      >
                        {claimable ? (claimingId === gid ? '...' : 'Забрать') : 'Получено'}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
